if (!customElements.get('nelko-image-banner-carousel')) {
  customElements.define(
    'nelko-image-banner-carousel',
    class NelkoImageBannerCarousel extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'))
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'))
        this.dotsRoot = this.querySelector('.nelko-banner-carousel__dots')
        this.previousButton = this.querySelector('[data-carousel-previous]')
        this.nextButton = this.querySelector('[data-carousel-next]')
        this.currentIndex = Math.max(
          this.slides.findIndex((slide) => slide.classList.contains('is-active')),
          0
        )
        this.autoplayEnabled = this.dataset.autoplay === 'true'
        this.interval = Math.max(Number.parseInt(this.dataset.interval, 10) || 5000, 3000)
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        this.touchStartX = 0
        this.timer = null
        this.prefetchQueued = false

        this.onPreviousClick = this.onPreviousClick.bind(this)
        this.onNextClick = this.onNextClick.bind(this)
        this.onDotClick = this.onDotClick.bind(this)
        this.onMouseEnter = this.onMouseEnter.bind(this)
        this.onMouseLeave = this.onMouseLeave.bind(this)
        this.onFocusIn = this.onFocusIn.bind(this)
        this.onFocusOut = this.onFocusOut.bind(this)
        this.onVisibilityChange = this.onVisibilityChange.bind(this)
        this.onTouchStart = this.onTouchStart.bind(this)
        this.onTouchEnd = this.onTouchEnd.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
        this.onBlockSelect = this.onBlockSelect.bind(this)
        this.onSlideClick = this.onSlideClick.bind(this)

        this.addEventListener('click', this.onSlideClick)

        if (this.slides.length < 2) return

        this.previousButton?.addEventListener('click', this.onPreviousClick)
        this.nextButton?.addEventListener('click', this.onNextClick)
        this.dotsRoot?.addEventListener('click', this.onDotClick)
        this.addEventListener('mouseenter', this.onMouseEnter)
        this.addEventListener('mouseleave', this.onMouseLeave)
        this.addEventListener('focusin', this.onFocusIn)
        this.addEventListener('focusout', this.onFocusOut)
        this.addEventListener('touchstart', this.onTouchStart, { passive: true })
        this.addEventListener('touchend', this.onTouchEnd, { passive: true })
        this.addEventListener('keydown', this.onKeyDown)
        document.addEventListener('visibilitychange', this.onVisibilityChange)
        document.addEventListener('shopify:block:select', this.onBlockSelect)

        this.updateSlides(this.currentIndex)
        this.queuePrefetch()
        this.startAutoplay()
      }

      disconnectedCallback() {
        this.stopAutoplay()
        this.previousButton?.removeEventListener('click', this.onPreviousClick)
        this.nextButton?.removeEventListener('click', this.onNextClick)
        this.dotsRoot?.removeEventListener('click', this.onDotClick)
        this.removeEventListener('mouseenter', this.onMouseEnter)
        this.removeEventListener('mouseleave', this.onMouseLeave)
        this.removeEventListener('focusin', this.onFocusIn)
        this.removeEventListener('focusout', this.onFocusOut)
        this.removeEventListener('touchstart', this.onTouchStart)
        this.removeEventListener('touchend', this.onTouchEnd)
        this.removeEventListener('keydown', this.onKeyDown)
        this.removeEventListener('click', this.onSlideClick)
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        document.removeEventListener('shopify:block:select', this.onBlockSelect)
      }

      onPreviousClick() {
        this.goTo(this.currentIndex - 1, true)
      }

      onNextClick() {
        this.goTo(this.currentIndex + 1, true)
      }

      onDotClick(event) {
        const dot = event.target.closest('[data-carousel-dot]')
        if (!dot || !this.dotsRoot?.contains(dot)) return
        this.goTo(Number(dot.dataset.carouselDot), true)
      }

      onMouseEnter() {
        this.stopAutoplay()
      }

      onMouseLeave() {
        this.startAutoplay()
      }

      onFocusIn() {
        this.stopAutoplay()
      }

      onFocusOut(event) {
        if (!this.contains(event.relatedTarget)) this.startAutoplay()
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.stopAutoplay()
        } else {
          this.startAutoplay()
        }
      }

      onTouchStart(event) {
        this.touchStartX = event.touches[0]?.clientX || 0
        this.stopAutoplay()
      }

      onTouchEnd(event) {
        const touchEndX = event.changedTouches[0]?.clientX || 0
        const distance = touchEndX - this.touchStartX

        if (Math.abs(distance) >= 45) {
          this.goTo(this.currentIndex + (distance < 0 ? 1 : -1), true)
        } else {
          this.startAutoplay()
        }
      }

      onKeyDown(event) {
        if (event.key === 'ArrowLeft') this.goTo(this.currentIndex - 1, true)
        if (event.key === 'ArrowRight') this.goTo(this.currentIndex + 1, true)
      }

      onBlockSelect(event) {
        const selectedSlide = this.slides.find((slide) => slide.dataset.blockId === event.detail?.blockId)
        if (!selectedSlide) return
        this.stopAutoplay()
        this.goTo(this.slides.indexOf(selectedSlide), false)
      }

      onSlideClick(event) {
        if (event.target.closest('[data-carousel-previous], [data-carousel-next], [data-carousel-dot]')) return
        if (event.target.closest('a, button')) return

        const slide = this.slides[this.currentIndex]
        const link = slide?.querySelector('.nelko-banner-carousel__slide-link[href]')
        const href = link?.getAttribute('href')
        if (!href || href === '#') return

        if (link.target === '_blank') {
          window.open(href, '_blank', 'noopener,noreferrer')
        } else {
          window.location.assign(href)
        }
      }

      goTo(index, restartAutoplay = false) {
        const nextIndex = (index + this.slides.length) % this.slides.length
        this.hydrateSlide(nextIndex)
        this.hydrateSlide(nextIndex + 1)
        this.updateSlides(nextIndex)

        if (restartAutoplay) {
          this.stopAutoplay()
          this.startAutoplay()
        }
      }

      hydrateSlide(index) {
        if (!this.slides.length) return

        const slide = this.slides[(index + this.slides.length) % this.slides.length]
        if (!slide || slide.dataset.hydrated === 'true') return

        const template = slide.querySelector('template[data-slide-media]')
        if (template) {
          template.replaceWith(template.content.cloneNode(true))
        }

        slide.dataset.hydrated = 'true'
      }

      queuePrefetch() {
        if (this.prefetchQueued || this.slides.length < 2) return
        this.prefetchQueued = true

        const prefetchNext = () => this.hydrateSlide(this.currentIndex + 1)

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(prefetchNext, { timeout: 2500 })
          return
        }

        window.addEventListener('load', prefetchNext, { once: true })
      }

      updateSlides(nextIndex) {
        this.currentIndex = nextIndex

        this.slides.forEach((slide, index) => {
          const isActive = index === nextIndex
          const slideLink = slide.querySelector('.nelko-banner-carousel__slide-link')

          slide.classList.toggle('is-active', isActive)
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true')
          if (slideLink) slideLink.tabIndex = isActive ? 0 : -1
        })

        this.dots.forEach((dot, index) => {
          const isActive = index === nextIndex
          dot.classList.toggle('is-active', isActive)
          if (isActive) {
            dot.setAttribute('aria-current', 'true')
          } else {
            dot.removeAttribute('aria-current')
          }
        })
      }

      startAutoplay() {
        if (!this.autoplayEnabled || this.reduceMotion || this.slides.length < 2 || document.hidden) return
        this.stopAutoplay()
        this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.interval)
      }

      stopAutoplay() {
        if (!this.timer) return
        window.clearInterval(this.timer)
        this.timer = null
      }
    }
  )
}
